import type { SessionDto } from "@bafe/api-client";
import { Button, Card, CopyField, Field, InlineNotice, Input, Section, StatusPill, useToast } from "@bafe/ui";
import { checkAccountPassword } from "@bafe/validation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router";

import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ErrorPanel } from "../../components/ErrorPanel";
import { PasswordField } from "../../components/PasswordField";
import { QrCode } from "../../components/QrCode";
import { api } from "../../lib/api";
import { queryKeys } from "../../lib/query";

export function SecuritySection({ session }: { session: SessionDto }) {
	return (
		<div className="flex flex-col gap-6">
			<ChangePassword mustChange={session.user.mustChangePassword} />
			<TwoFactor session={session} />

			<Section title="Signed-in devices">
				<Card>
					<p className="text-sm text-(--text-secondary)">
						Review where your account is signed in, and sign out anything you do not recognise, under{" "}
						<Link to="/manage/account/sessions" className="underline underline-offset-2">
							Sessions
						</Link>
						.
					</p>
				</Card>
			</Section>
		</div>
	);
}

function ChangePassword({ mustChange }: { mustChange: boolean }) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [submitted, setSubmitted] = useState(false);

	const check = checkAccountPassword(newPassword);

	const change = useMutation({
		mutationFn: () => api.changePassword({ currentPassword, newPassword }),
		onSuccess: () => {
			setCurrentPassword("");
			setNewPassword("");
			setSubmitted(false);
			void queryClient.invalidateQueries({ queryKey: queryKeys.session });
			toast({ tone: "success", message: "Your Key password is changed." });
		},
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSubmitted(true);
		if (!check.ok) return;
		change.mutate();
	}

	return (
		<Section
			title="Key account password"
			description="This is the password for the control panel. It is separate from your mailbox passwords."
		>
			{mustChange && (
				<InlineNotice tone="caution" title="Choose a new password">
					Your administrator set a temporary password. Change it before you continue.
				</InlineNotice>
			)}
			<form className="flex max-w-lg flex-col gap-4" onSubmit={submit} noValidate>
				<PasswordField
					label="Current password"
					value={currentPassword}
					onChange={setCurrentPassword}
					autoComplete="current-password"
					required
				/>

				<PasswordField
					label="New password"
					value={newPassword}
					onChange={setNewPassword}
					error={submitted && !check.ok ? (check.message ?? null) : null}
					help="At least 10 characters. Longer is better than complicated."
					autoComplete="new-password"
					required
				/>

				{change.isError && <ErrorPanel error={change.error} title="We could not change your password." />}

				<div>
					<Button
						type="submit"
						variant="primary"
						pending={change.isPending}
						pendingLabel="Saving…"
						disabled={currentPassword.length === 0 || newPassword.length === 0}
					>
						Change password
					</Button>
				</div>
			</form>
		</Section>
	);
}

function TwoFactor({ session }: { session: SessionDto }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const [code, setCode] = useState("");
	const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
	const [disabling, setDisabling] = useState(false);
	const [disablePassword, setDisablePassword] = useState("");

	const begin = useMutation({ mutationFn: () => api.beginTotpSetup() });

	const confirm = useMutation({
		mutationFn: () => api.confirmTotpSetup(code.trim().replace(/\s+/g, "")),
		onSuccess: (result) => {
			setRecoveryCodes(result.codes);
			setCode("");
			begin.reset();
			void queryClient.invalidateQueries({ queryKey: queryKeys.session });
			toast({ tone: "success", message: "Two-factor authentication is on." });
		},
	});

	const disable = useMutation({
		mutationFn: () => api.disableTotp(disablePassword),
		onSuccess: () => {
			setDisabling(false);
			setDisablePassword("");
			void queryClient.invalidateQueries({ queryKey: queryKeys.session });
			toast({ tone: "success", message: "Two-factor authentication is off." });
		},
	});

	const regenerate = useMutation({
		mutationFn: () => api.regenerateRecoveryCodes(),
		onSuccess: (result) => {
			setRecoveryCodes(result.codes);
			toast({ tone: "success", message: "New recovery codes generated. The old ones no longer work." });
		},
	});

	return (
		<Section
			title="Two-factor authentication"
			description="A second step at sign-in, so a stolen password is not enough on its own."
			action={
				session.user.twoFactorEnabled ? (
					<StatusPill tone="positive">On</StatusPill>
				) : (
					<StatusPill tone="caution">Off</StatusPill>
				)
			}
		>
			{recoveryCodes && (
				<Card className="flex flex-col gap-3">
					<div className="flex flex-col gap-1">
						<h3 className="text-sm font-semibold text-(--text-primary)">Your recovery codes</h3>
						<p className="text-sm text-(--text-secondary)">
							Each code works once, and only if you lose your authenticator app. Save them somewhere
							other than your phone.
						</p>
					</div>

					<CopyField value={recoveryCodes.join("\n")} label="All recovery codes" />

					<ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
						{recoveryCodes.map((recoveryCode) => (
							<li
								key={recoveryCode}
								className="rounded-(--radius-sm) bg-(--surface-sunken) px-2.5 py-1.5 text-dns text-(--text-primary)"
							>
								{recoveryCode}
							</li>
						))}
					</ul>

					<InlineNotice tone="caution" title="These will not be shown again">
						Key stores them in a form it cannot reverse. If you lose them, generate a new set.
					</InlineNotice>

					<div>
						<Button variant="ghost" onClick={() => setRecoveryCodes(null)}>
							I have saved them
						</Button>
					</div>
				</Card>
			)}

			{!session.user.twoFactorEnabled && !begin.data && (
				<div className="flex flex-col gap-3">
					{begin.isError && (
						<ErrorPanel error={begin.error} title="We could not start two-factor setup." />
					)}
					<div>
						<Button
							variant="primary"
							pending={begin.isPending}
							pendingLabel="Preparing…"
							onClick={() => begin.mutate()}
						>
							Turn on two-factor authentication
						</Button>
					</div>
				</div>
			)}

			{begin.data && (
				<Card className="flex flex-col gap-5">
					<div className="flex flex-col gap-1">
						<h3 className="text-sm font-semibold text-(--text-primary)">
							Scan this with your authenticator app
						</h3>
						<p className="text-sm text-(--text-secondary)">
							Use any authenticator app. Scan the code, then enter the six digits it shows.
						</p>
					</div>

					<div className="flex flex-col gap-4 sm:flex-row sm:items-start">
						<QrCode value={begin.data.otpauthUri} label="Two-factor setup QR code for Key" />

						<div className="flex min-w-0 flex-1 flex-col gap-2">
							<p className="text-xs font-medium text-(--text-muted)">
								Cannot scan? Enter this key by hand
							</p>
							<CopyField value={begin.data.secret} label="Setup key" />
						</div>
					</div>

					<form
						className="flex max-w-sm flex-col gap-3"
						onSubmit={(event) => {
							event.preventDefault();
							confirm.mutate();
						}}
						noValidate
					>
						<Field label="Six-digit code" required>
							{({ id, describedBy, invalid }) => (
								<Input
									id={id}
									value={code}
									required
									inputMode="numeric"
									autoComplete="one-time-code"
									className="text-dns"
									aria-describedby={describedBy}
									aria-invalid={invalid}
									onChange={(event) => setCode(event.target.value)}
								/>
							)}
						</Field>

						{confirm.isError && (
							<ErrorPanel error={confirm.error} title="That code did not work." />
						)}

						<div className="flex flex-wrap gap-2">
							<Button
								type="submit"
								variant="primary"
								pending={confirm.isPending}
								pendingLabel="Checking…"
							>
								Confirm and turn on
							</Button>
							<Button variant="ghost" onClick={() => begin.reset()} disabled={confirm.isPending}>
								Cancel
							</Button>
						</div>
					</form>
				</Card>
			)}

			{session.user.twoFactorEnabled && (
				<div className="flex flex-col gap-3">
					{regenerate.isError && (
						<ErrorPanel error={regenerate.error} title="We could not generate new recovery codes." />
					)}

					<div className="flex flex-wrap gap-2">
						<Button
							variant="secondary"
							pending={regenerate.isPending}
							pendingLabel="Generating…"
							onClick={() => regenerate.mutate()}
						>
							Generate new recovery codes
						</Button>
						<Button variant="ghost" onClick={() => setDisabling(true)}>
							Turn off two-factor authentication
						</Button>
					</div>
				</div>
			)}

			<ConfirmDialog
				open={disabling}
				onClose={() => {
					setDisabling(false);
					disable.reset();
				}}
				title="Turn off two-factor authentication?"
				description="Your password alone will be enough to sign in. We do not recommend this for accounts that manage email."
				confirmLabel="Turn it off"
				danger
				pending={disable.isPending}
				error={disable.error}
				onConfirm={() => {
					if (disablePassword.length > 0) disable.mutate();
				}}
			>
				<PasswordField
					label="Your Key password"
					value={disablePassword}
					onChange={setDisablePassword}
					autoComplete="current-password"
					required
				/>
			</ConfirmDialog>
		</Section>
	);
}
